import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './GraphicCompanyScrolling.css';

import depEnv from '../images/pg-county-logo.png';
import depArmy from '../images/dep-army-logo.png';
import cityBalt from '../images/cityofbalt-logo.png';
import corpsArmy from '../images/army-corps.png';
import army from '../images/army-logo.png';
import dpw from '../images/dpw-logo.jpg';
import usps from '../images/usps-logo.png';
import dcWater from '../images/DC_Water_logo.png';
import wssc from '../images/wssc.jpg';
import howardCounty from '../images/Seal_of_Howard_County,_Maryland.png';
import mdot from '../images/mdot.png';
import mta from '../images/mta.png';
import mtaTransit from '../images/mta transit admin.png';
import mdEnv from '../images/md enviro services.png';
import baltCounty from '../images/balt county.png';

export default function GraphicCompanyScrolling({
  items,
  height,
  speed = 'slow',
  gap = 20,
  monochrome = false,
  title = 'Agencies We Work With',
  to = '/our-work',
  className = '',
}) {
  const DEFAULT_LOGOS = [
    { src: depEnv, alt: 'Department of Environment' },
    { src: depArmy, alt: 'Department of the Army' },
    { src: cityBalt, alt: 'City of Baltimore' },
    { src: corpsArmy, alt: 'US Army Corps of Engineers' },
    { src: army, alt: 'US Army' },
    { src: dpw, alt: 'Department of Public Works' },
    { src: usps, alt: 'United States Postal Service' },
    { src: dcWater, alt: 'DC Water' },
    { src: wssc, alt: 'WSSC' },
    { src: howardCounty, alt: 'Howard County' },
    { src: mdot, alt: 'MDOT State Highway Administration', scale: 1.3 },
    { src: mta, alt: 'MTA' },
    { src: mtaTransit, alt: 'MTA Transit' },
    { src: mdEnv, alt: 'Maryland Environmental Service' },
    { src: baltCounty, alt: 'Baltimore County' },
  ];

  const logos = Array.isArray(items) && items.length ? items : DEFAULT_LOGOS;
  const trackItems = useMemo(() => [...logos, ...logos], [logos]);

  const speedPx = useMemo(() => {
    if (typeof speed === 'number' && speed > 0) return speed;
    const s = String(speed).toLowerCase();
    if (s === 'slow') return 50;
    if (s === 'fast') return 140;
    return 90;
  }, [speed]);

  const styleVars = {
    '--item-gap': `${gap}px`,
    '--img-filter': monochrome ? 'grayscale(1) brightness(1.05) contrast(1.05)' : 'none',
    ...(height ? { '--row-height': typeof height === 'number' ? `${height}px` : height } : {}),
  };

  const railRef = useRef(null);
  const posRef = useRef(0);
  const halfRef = useRef(0);
  const rafRef = useRef(0);
  const lastT = useRef(0);

  const [broken, setBroken] = useState(new Set());
  const onImgError = (src) => {
    setBroken((prev) => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  };

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const measure = () => {
      const full = rail.scrollWidth;
      halfRef.current = full / 2;
      if (halfRef.current > 0 && posRef.current < -halfRef.current) {
        posRef.current = ((posRef.current % -halfRef.current) + -halfRef.current) % -halfRef.current;
      }
    };

    const onResize = () => {
      const x = posRef.current;
      measure();
      rail.style.transform = `translate3d(${x}px,0,0)`;
    };

    const imgs = rail.querySelectorAll('img');
    let pending = imgs.length;
    const onLoaded = () => {
      pending -= 1;
      if (pending <= 0) measure();
    };
    imgs.forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', onLoaded, { once: true });
      img.addEventListener('error', onLoaded, { once: true });
    });
    measure();

    const loop = (t) => {
      if (prefersReduce) return;
      if (!lastT.current) lastT.current = t;
      const dt = (t - lastT.current) / 1000;
      lastT.current = t;

      const hw = halfRef.current || 0;
      let x = posRef.current - speedPx * dt;
      if (hw > 0 && x <= -hw) x += hw;
      posRef.current = x;

      rail.style.transform = `translate3d(${x}px,0,0)`;
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      imgs.forEach((img) => {
        img.removeEventListener('load', onLoaded);
        img.removeEventListener('error', onLoaded);
      });
    };
  }, [trackItems, speedPx]);

  return (
    <section className={`gcs-container ${className}`.trim()} style={styleVars} aria-label={title}>
      <h2 className="gcs-title">{title}</h2>
      <div className="gcs-fade gcs-fade--left" aria-hidden="true" />
      <div className="gcs-fade gcs-fade--right" aria-hidden="true" />
      <div className="gcs-viewport">
        <ul className="gcs-track" ref={railRef}>
          {trackItems.map((logo, idx) => {
            const key = `${logo.src}-${idx}`;
            if (broken.has(logo.src)) return null;
            return (
              <li className="gcs-item" key={key}>
                <Link to={to} className="gcs-link" aria-label={`Open Our Work – ${logo.alt || 'Partner'}`}>
                  <img
                    src={logo.src}
                    alt={logo.alt || 'Partner logo'}
                    className="gcs-img"
                    style={{ '--scale': logo.scale || 1 }}
                    loading="lazy"
                    decoding="async"
                    onError={() => onImgError(logo.src)}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
